import { useTranslation } from 'react-i18next';

/**
 * 측정 직후 확인 다이얼로그(덱 p7 마지막 프레임).
 *
 * 재시도를 **세그먼트 단위**로 두는 이유(티켓 12): 마지막에 몰아서 판정하면 세 번째
 * 진단에서 리포트가 무효일 때 전부 다시 찍어야 한다. 방금 잰 값을 바로 보여주고 그
 * 자리에서 다시 하게 한다.
 *
 * 덱은 값 없이 "완료되었어요"만 띄우지만 **측정값을 남긴다** — 값이 없으면 "재진단"을
 * 누를지 말지 찍어서 정해야 한다.
 *
 * 다시 해도 이전 시도를 **버리지 않는다**: MPT·음역은 시도들의 최댓값을 쓰므로
 * (framesToClips), 못한 재시도가 이미 낸 기록을 깎지 않는다.
 */

interface RecordReviewViewProps {
  /** 다이얼로그 제목 — 진단이 끝났으면 완료 문장, 아니면 다음 세그먼트 안내. */
  title: string;
  /** 제목 아래 한 줄. 진단 마지막에서만 있다. */
  hint: string | null;
  /** 방금 시도 요약(예: `12.4초 발성`, `최저 E2`). */
  summary: string;
  /** 이 시도로 지표가 안 나올 것 같을 때의 안내. 전진을 막지는 않는다. */
  warning: string | null;
  /** 몇 번째 시도인지 — 2회차부터만 보인다. */
  attemptCount: number;
  onRetry: () => void;
  onConfirm: () => void;
}

export const RecordReviewView = ({
  title,
  hint,
  summary,
  warning,
  attemptCount,
  onRetry,
  onConfirm,
}: RecordReviewViewProps) => {
  const { t } = useTranslation();

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-foreground/30 px-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="record-review-title"
        className="w-full max-w-xs overflow-hidden rounded-2xl bg-background"
      >
        <div className="flex flex-col items-center gap-1 px-5 py-6 text-center">
          <p id="record-review-title" className="font-bold leading-snug">
            {title}
          </p>
          {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
          <p className="mt-2 text-xs text-muted-foreground">
            {t('web.diagnosis.review.heading')}
          </p>
          <p className="text-2xl font-bold tabular-nums">{summary}</p>
          {warning && (
            <p className="mt-1 text-sm text-destructive">{warning}</p>
          )}
          {attemptCount > 1 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('web.diagnosis.review.attempts', { value: attemptCount })}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 border-t border-border">
          <button
            type="button"
            onClick={onRetry}
            className="min-h-[44px] border-r border-border py-3.5 font-medium text-muted-foreground"
          >
            {t('web.diagnosis.review.retry')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            // 시안은 두 버튼 다 중립색이고 무게로만 가른다 — 측정 화면의 CTA가 전부
            // 검정으로 바뀐 것과 같은 맥락이라 여기만 브랜드색을 쓰면 튄다.
            className="min-h-[44px] py-3.5 font-bold text-foreground"
          >
            {t('web.diagnosis.review.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
